import Vue from 'vue';
import { mapState } from 'vuex';
import {
	KitType,
	KitToolLabel,
	ResolvedKit,
	RunnitNodeDef,
	resolveKits,
	allKitNodeDefIds,
} from '@run-diffusion/shared';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import { getKitPreference } from '@/utils/localStorage';

/**
 * Kits, resolved. The single place web turns the realtime `runnitKits`
 * binding plus the visible RunnitNodeDefs into rendered Kits, through the
 * shared Kit Resolver.
 *
 * Before this, RunnitToolsLibraryDialog and RunnitSingleTool each had their
 * own `kitOptions` and disagreed about missing and team-blocked tools
 * (CONTEXT.md called it a known defect). Both now read `resolvedKitsByType`,
 * so there is one answer to "what is in this Kit".
 */
export const KitsMixin = Vue.extend({
	mixins: [
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'runnitKits',
			'loadingRunnitKits',
			'publicRunnitNodeDefs',
			'companyRunnitNodeDefs',
		]),
		/**
		 * Kits used to be synchronous constants, so a consumer could route on
		 * a default tool during first paint. They arrive over a binding now:
		 * gate anything that needs a default on this instead of rendering a
		 * broken or empty Kit.
		 */
		kitsLoaded (): boolean {
			return this.loadingRunnitKits === false;
		},
		allVisibleNodeDefs (): RunnitNodeDef[] {
			return [
				...(this.publicRunnitNodeDefs || []),
				...(this.companyRunnitNodeDefs || []),
			];
		},
		resolvedKits (): ResolvedKit[] {
			return resolveKits({
				kits: this.runnitKits || [],
				nodeDefs: this.allVisibleNodeDefs as any,
				isBlocked: (nodeDef) => this.isNodeDefBlockedForTeamUser(nodeDef as unknown as RunnitNodeDef),
				surface: 'web',
			});
		},
		resolvedKitsByType (): Record<string, ResolvedKit> {
			return (this.resolvedKits as ResolvedKit[]).reduce((acc, kit) => {
				acc[kit.id] = kit;
				return acc;
			}, {} as Record<string, ResolvedKit>);
		},
		/**
		 * The Kits a user can actually open: visible, and with at least one
		 * visible tool. A Kit whose every tool is unpublished or blocked for
		 * this team resolves fine but leads to an empty picker, so entry points
		 * (nav, home tiles, the Kit cards) must not offer it.
		 */
		navigableKits (): ResolvedKit[] {
			return (this.resolvedKits as ResolvedKit[]).filter((kit) => !!kit.tools.length);
		},
		/**
		 * Every curated nodeDefId across all Kits. Membership checks only, so
		 * duplicates are expected. Replaces the old getAllKitIds().
		 */
		validKitIds (): string[] {
			return allKitNodeDefIds(this.runnitKits || []);
		},
	},
	methods: {
		getResolvedKit (kitType: KitType | string | null): ResolvedKit | null {
			if (!kitType) return null;
			return this.resolvedKitsByType[kitType] || null;
		},
		/**
		 * The tool a Kit opens with when the user has no stored preference.
		 * Null until the binding loads, so callers must gate on kitsLoaded.
		 *
		 * The resolver already falls back to the Kit's first VISIBLE tool when
		 * the curated default is unpublished, deleted, or blocked for this
		 * team, so this never returns a tool the user cannot open.
		 */
		getKitDefaultNodeDefId (kitType: KitType | string): string | null {
			return this.getResolvedKit(kitType)?.defaultNodeDefId || null;
		},
		/**
		 * The tool to open when a user enters a Kit: their last-used tool in
		 * that Kit if it is still visible to them, otherwise the Kit's default.
		 *
		 * The stored preference is checked against THIS Kit's resolved tools,
		 * not the flat list of every curated id. The old check accepted a tool
		 * curated in any Kit, and never re-checked that it was still visible,
		 * so a preference could survive the tool being unpublished, removed
		 * from the Kit, or blocked for the team.
		 */
		getKitEntryNodeDefId (kitType: KitType | string): string | null {
			const kit = this.getResolvedKit(kitType);
			if (!kit) return null;

			const stored = getKitPreference(kitType as string);
			const storedIsVisibleHere = !!stored && kit.tools.some((tool) => tool.nodeDefId === stored);
			return storedIsVisibleHere ? stored : kit.defaultNodeDefId;
		},
		/**
		 * The Kit Tool Labels curated for a tool. A tool in more than one Kit
		 * takes the first Kit that labels it, matching the old hardcoded
		 * lookup that scanned the kit arrays in order.
		 */
		getKitToolLabels (nodeDefId: string): KitToolLabel[] | null {
			if (!nodeDefId) return null;
			for (const kit of this.resolvedKits as ResolvedKit[]) {
				const tool = kit.tools.find((entry) => entry.nodeDefId === nodeDefId);
				if (tool && tool.labels.length) return tool.labels;
			}
			return null;
		},
	},
});
